import type { ComponentProps } from 'react'
import { Button } from 'components/Button'
import { Icon } from 'components/Icon'
import { adCodeStrings } from 'constants/strings'
import { useCopyToClipboard } from 'hooks/useCopyToClipboard'

type Props = {
  /** コピーする発行URL。 */
  url: string
  /** 待機中に出すラベル。一覧は「コピー」、詳細は「URLをコピー」。 */
  idleLabel: string
  variant?: ComponentProps<typeof Button>['variant']
  className?: string
  /** 行クリックで詳細へ遷移する一覧では、伝播を止めるために使う。 */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  /** コピーに成功したときだけ呼ばれる。一覧・詳細のSnackbar表示に使う。 */
  onCopied?: () => void
}

/**
 * 発行URLのコピーボタン。
 *
 * 押すとボタン内の文字が「コピーしました」に変わり、2秒で元へ戻る（ヘッダーの
 * 友だち追加URLコピーと同じ挙動）。状態はボタンごとに持つので、一覧では押した行
 * だけが変わる。
 */
export function AdCodeCopyUrlButton({
  url,
  idleLabel,
  variant = 'primary',
  className,
  onClick,
  onCopied,
}: Props) {
  const { status, copy } = useCopyToClipboard()

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event)
    const copied = await copy(url)
    if (copied) onCopied?.()
  }

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant={variant}
        className={className}
        onClick={handleClick}
      >
        {status === 'success' ? (
          <>
            <Icon icon="check" className="mr-1 h-3.5 w-3.5" />
            {adCodeStrings.detail.copySuccess}
          </>
        ) : status === 'error' ? (
          adCodeStrings.detail.copyError
        ) : (
          idleLabel
        )}
      </Button>
      <span aria-live="polite" className="sr-only">
        {status === 'success'
          ? adCodeStrings.detail.copySuccess
          : status === 'error'
            ? adCodeStrings.detail.copyError
            : ''}
      </span>
    </>
  )
}
